import React, { useState } from 'react';
import { View } from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { MainLayout } from '@shared/components/md-design-system/Layout/MainLayout';
import { Head } from '@shared/components/head';
import { Text } from '@shared/components/text';
import { DEVICE_ISSUE_TYPE, TDeviceIssueType, TDeviceIssueVerifyOutcome } from 'app/types/deviceIssue';
import { DEVICE_ISSUE_CONFIG } from '../constants/deviceIssueConfig';
import { useVerifyDeviceIssue } from '../hooks/useVerifyDeviceIssue';
import { CSHotlineCTA, DeviceTroubleShootingProblemInfoBox, DeviceTroubleshootingSteps } from '../components';
import Button from '@md-design-system/Button/Button';
import { MediaViewer } from '@shared/components/MediaViewer';
import { remoteConfigManager } from 'app/services/RemoteConfigManager';
import { useDataFreshness, usePathwayId } from 'app/_modules/pathways';

type DeviceTroubleshootingParams = { issueType: TDeviceIssueType };
type DeviceTroubleshootingRouteProp = RouteProp<Record<string, DeviceTroubleshootingParams>, string>;
type DeviceTroubleshootingNavigationProp = NativeStackNavigationProp<any>;

export const DeviceTroubleshootingScreen = () => {
  const navigation = useNavigation<DeviceTroubleshootingNavigationProp>();
  const route = useRoute<DeviceTroubleshootingRouteProp>();
  const { issueType } = route.params;
  const { t } = dynamicTranslations();
  const { verify, isVerifying } = useVerifyDeviceIssue();
  const pathwayId = usePathwayId();
  const isGatewayIssue = issueType === DEVICE_ISSUE_TYPE.gatewayDisconnected;
  const { refetch: refetchDataFreshness, isFetching: isVerifyingGateway } = useDataFreshness(pathwayId, {
    enabled: false,
  });
  const [verifyError, setVerifyError] = useState(false);

  const config = DEVICE_ISSUE_CONFIG[issueType];
  const verifyCopy = config.page.verify;
  const mediaUrl = config.page.mediaConfig
    ? remoteConfigManager.getString(config.page.mediaConfig.remoteConfigKey, '')
    : undefined;

  const handleBackToHome = () => {
    navigation.navigate('Homepage');
  };

  const showResult = (outcome: TDeviceIssueVerifyOutcome) => {
    navigation.navigate('DeviceTroubleshootingResult', { issueType, outcome });
  };

  const handleVerify = async () => {
    setVerifyError(false);

    if (isGatewayIssue) {
      const { data, isError } = await refetchDataFreshness();
      if (isError || !data) {
        setVerifyError(true);
        return;
      }
      showResult(data.isGatewayConnected ? 'success' : 'failure');
      return;
    }

    try {
      const { resolved } = await verify(issueType);
      showResult(resolved ? 'success' : 'failure');
    } catch {
      setVerifyError(true);
    }
  };

  return (
    <MainLayout navigation={handleBackToHome}>
      <View className="flex-1 h-full flex-col">
        <View className='flex-col gap-4 flex-1'>
          <Head type="h2">{t(config.page.titleKey)}</Head>
          {mediaUrl && (
            <View className="w-full rounded-lg overflow-hidden" style={{ height: 350 }}>
              <MediaViewer
                mediaUrl={mediaUrl}
                style={{ height: 256 }}
                resizeMode="contain"
                controls={true}
                paused={false}
                repeat={false}
              />
            </View>
          )}

          <Text>{t(config.page.introKey)}</Text>

          <DeviceTroubleShootingProblemInfoBox {...config} />
          <DeviceTroubleshootingSteps {...config} />
        </View>
        <View className='flex-col gap-4'>
          {verifyError && (
            <Text className="text-warning-500">{t('screens.deviceTroubleshooting.verifyError')}</Text>
          )}
          {verifyCopy && (
            <View className="w-full">
              <Button variant="primary" onPress={handleVerify} disabled={isVerifying || isVerifyingGateway}>
                {isVerifying || isVerifyingGateway
                  ? t('screens.deviceTroubleshooting.verifying')
                  : t('screens.deviceTroubleshooting.verifyCta')}
              </Button>
            </View>
          )}
          <CSHotlineCTA />
        </View>
      </View>
    </MainLayout>
  );
};

export default DeviceTroubleshootingScreen;
